import { useGLTF } from "@react-three/drei";
import React, { FC, Fragment, Suspense, useEffect, useMemo } from "react";
import { CollisionObject } from "./Features/Interaction/CollisionObject";
import {
  hasCollisionReplacingContext,
  MeshFeatureContext,
} from "./Features/MeshFeatureContext";
import { CurrentModelProvider } from "./Features/CurrentModelContext";
import {
  AnimationsController,
  useAnimationsController,
} from "./Features/Animation/AnimationsController";
import {
  applyContextPresets,
  getPropertyObjectContextName,
  getUserDataProperties,
} from "./Features/FeatureUtils";
import { InteractionContextPresets } from "./Features/Interaction/ContextPresets";
import { CollisionObjectProps } from "./Features/Interaction/CollisionTypes";
import { useSceneContext } from "./Features/Environmental/SceneController";
import { LODModel } from "./LODModel";

const fourPartArrayToColor = (floatVector: number[]): string => {
  if (floatVector.length !== 4) {
    throw new Error("Input array must have 4 components (RGBA).");
  }

  const [r, g, b, a] = floatVector;
  const rHex = Math.floor(r * 255)
    .toString(16)
    .padStart(2, "0");
  const gHex = Math.floor(g * 255)
    .toString(16)
    .padStart(2, "0");
  const bHex = Math.floor(b * 255)
    .toString(16)
    .padStart(2, "0");
  const aHex = Math.floor(a * 255)
    .toString(16)
    .padStart(2, "0");

  return `#${rHex}${gHex}${bHex}${aHex}`;
};

const lightblueRGBA: number[] = [173 / 255, 216 / 255, 230 / 255, 1];

const AnimationPlayer: FC = () => {
  const { allAnimations, playAnimations, stopAnimations } =
    useAnimationsController();

  useEffect(() => {
    playAnimations(allAnimations);

    return () => stopAnimations(allAnimations);
  }, [allAnimations, playAnimations, stopAnimations]);

  return undefined;
};

export type GLTFModelProps = {
  url: string;
};

export const GLTFModel: FC<GLTFModelProps> = ({ url = "" }) => {
  const gltfModel = useGLTF(url);
  // @ts-ignore
  const { nodes, scene } = gltfModel;
  const nodeArray = useMemo(() => Object.values(nodes), [nodes]);
  const { setSkyColor } = useSceneContext();

  useEffect(() => {
    const { SkyColor: skyColorFourPartArray = lightblueRGBA } =
      getUserDataProperties(scene.userData, "Scene");
    const skyColor = fourPartArrayToColor(skyColorFourPartArray);

    setSkyColor(skyColor);
  }, [scene, setSkyColor]);

  return (
    <Suspense>
      <CurrentModelProvider value={gltfModel}>
        <AnimationsController>
          <AnimationPlayer />
          <LODModel>
            <primitive object={scene} />
            {nodeArray.map((node: any, index) => {
              const { userData = {} } = node || {};
              const meshContextList = Object.keys(
                userData[getPropertyObjectContextName()] || {}
              );
              const interactionContextProps = applyContextPresets<
                Partial<CollisionObjectProps>
              >({}, userData, InteractionContextPresets);
              const { collisionShape = undefined } = interactionContextProps;
              const useCollision =
                !!collisionShape &&
                !hasCollisionReplacingContext(meshContextList);

              return (
                <Fragment key={index}>
                  {useCollision ? (
                    <CollisionObject
                      collisionShape={collisionShape}
                      {...interactionContextProps}
                      meshNode={node}
                    />
                  ) : undefined}
                  {meshContextList.map((mc, i) => (
                    <MeshFeatureContext
                      key={`${mc}:${i}`}
                      mesh={node}
                      context={mc}
                      userData={userData}
                    />
                  ))}
                </Fragment>
              );
            })}
          </LODModel>
        </AnimationsController>
      </CurrentModelProvider>
    </Suspense>
  );
};
